import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send,
  Link2,
  Tag,
  Settings2,
  AlertCircle,
  Wrench,
  X,
  Loader,
  SmilePlus,
  Copy,
  Check,
  Zap,
  Sparkles,
} from 'lucide-react';
import { POPULAR_TOOLS } from '../constants/taskCreation';
import {
  createPromptPost,
  subscribePromptPosts,
  addReaction,
  STATUS_OPTIONS,
} from '../api/promptPosts';
import type { PromptPost, PromptTag, PromptTagType, StatusOption } from '../api/promptPosts';

// ─── Helpers ────────────────────────────────────────────────────────────────

function relativeTime(ts: PromptPost['createdAt'] | undefined): string {
  if (!ts) return 'just now';
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function autoLinkText(text: string): React.ReactNode[] {
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlPattern);
  return parts.map((part, i) =>
    urlPattern.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-cyan-400 underline underline-offset-2 break-all hover:text-cyan-300 transition-colors"
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

// ─── Tag chip styles ─────────────────────────────────────────────────────────

const STATUS_CHIP_STYLES: Record<StatusOption, string> = {
  blocked:        'bg-red-500/20 border-red-500/40 text-red-300 shadow-[0_0_6px_rgba(239,68,68,0.2)]',
  done:           'bg-emerald-500/20 border-emerald-500/40 text-emerald-300 shadow-[0_0_6px_rgba(52,211,153,0.2)]',
  'review-needed':'bg-orange-500/20 border-orange-500/40 text-orange-300 shadow-[0_0_6px_rgba(251,146,60,0.2)]',
  question:       'bg-violet-500/20 border-violet-500/40 text-violet-300 shadow-[0_0_6px_rgba(167,139,250,0.2)]',
};

const STATUS_LABELS: Record<StatusOption, string> = {
  blocked: 'Blocked',
  done: 'Done',
  'review-needed': 'Review Needed',
  question: 'Question',
};

const TAG_ACCENT: Record<PromptTagType, string> = {
  link:    'from-cyan-500 to-blue-500',
  keyword: 'from-violet-500 to-purple-500',
  env_var: 'from-amber-500 to-orange-500',
  status:  'from-rose-500 to-pink-500',
  tool:    'from-teal-500 to-emerald-500',
};

function TagChip({ tag, onRemove }: { tag: PromptTag; onRemove?: () => void }) {
  if (tag.type === 'link') {
    const display = (() => {
      try {
        const u = new URL(tag.value);
        return u.hostname + (u.pathname.length > 1 ? u.pathname.slice(0, 20) + '…' : '');
      } catch {
        return tag.value.slice(0, 30) + (tag.value.length > 30 ? '…' : '');
      }
    })();
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium bg-cyan-500/10 border-cyan-500/30 text-cyan-300 shadow-[0_0_8px_rgba(6,182,212,0.15)]">
        <Link2 className="w-3 h-3 shrink-0" />
        {onRemove ? (
          <span>{display}</span>
        ) : (
          <a href={tag.value} target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-cyan-200 transition-colors">
            {display}
          </a>
        )}
        {onRemove && (
          <button type="button" onClick={onRemove} className="ml-0.5 hover:text-white opacity-60 hover:opacity-100 transition-opacity">
            <X className="w-3 h-3" />
          </button>
        )}
      </span>
    );
  }

  if (tag.type === 'keyword') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium bg-violet-500/10 border-violet-500/30 text-violet-300 shadow-[0_0_8px_rgba(139,92,246,0.15)]">
        <Tag className="w-3 h-3 shrink-0" />
        #{tag.value}
        {onRemove && (
          <button type="button" onClick={onRemove} className="ml-0.5 hover:text-white opacity-60 hover:opacity-100 transition-opacity">
            <X className="w-3 h-3" />
          </button>
        )}
      </span>
    );
  }

  if (tag.type === 'env_var') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-mono bg-amber-500/10 border-amber-500/30 text-amber-300 shadow-[0_0_8px_rgba(245,158,11,0.15)]">
        <Settings2 className="w-3 h-3 shrink-0" />
        ${tag.value}
        {onRemove && (
          <button type="button" onClick={onRemove} className="ml-0.5 hover:text-white opacity-60 hover:opacity-100 transition-opacity">
            <X className="w-3 h-3" />
          </button>
        )}
      </span>
    );
  }

  if (tag.type === 'status') {
    const slug = tag.value as StatusOption;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${STATUS_CHIP_STYLES[slug] ?? 'bg-white/10 border-white/20 text-tech-text'}`}>
        <AlertCircle className="w-3 h-3 shrink-0" />
        {STATUS_LABELS[slug] ?? tag.value}
        {onRemove && (
          <button type="button" onClick={onRemove} className="ml-0.5 hover:text-white opacity-60 hover:opacity-100 transition-opacity">
            <X className="w-3 h-3" />
          </button>
        )}
      </span>
    );
  }

  if (tag.type === 'tool') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium bg-teal-500/10 border-teal-500/30 text-teal-300 shadow-[0_0_8px_rgba(20,184,166,0.15)]">
        <Wrench className="w-3 h-3 shrink-0" />
        {tag.value}
        {onRemove && (
          <button type="button" onClick={onRemove} className="ml-0.5 hover:text-white opacity-60 hover:opacity-100 transition-opacity">
            <X className="w-3 h-3" />
          </button>
        )}
      </span>
    );
  }

  return null;
}

// ─── Emoji palette ───────────────────────────────────────────────────────────

const QUICK_EMOJIS = ['👍', '🔥', '✅', '❓', '🚀', '⚠️', '💡', '👀'];

function EmojiPalette({
  postId,
  reactions = {},
  currentUserId,
  onClose,
}: {
  postId: string;
  reactions?: Record<string, string[]>;
  currentUserId: string;
  onClose: () => void;
}) {
  return (
    <div className="absolute bottom-full left-0 mb-2 flex gap-1 p-2 rounded-xl bg-[#0a0f1e] border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] z-10 backdrop-blur-sm">
      {QUICK_EMOJIS.map((e) => {
        const hasReacted = (reactions[e] || []).includes(currentUserId);
        return (
          <button
            key={e}
            type="button"
            onClick={() => {
              addReaction(postId, e, currentUserId, hasReacted).catch(console.error);
              onClose();
            }}
            className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm hover:bg-white/10 transition-all hover:scale-110 ${hasReacted ? 'bg-white/20 ring-1 ring-white/30' : ''}`}
          >
            {e}
          </button>
        );
      })}
    </div>
  );
}

// ─── Post card ───────────────────────────────────────────────────────────────

function PostCard({
  post,
  index,
  currentUserId,
}: {
  post: PromptPost;
  index: number;
  currentUserId: string;
}) {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const emojiRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!emojiOpen) return;
    const handler = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [emojiOpen]);

  const handleCopy = () => {
    navigator.clipboard.writeText(post.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(console.error);
  };

  const initials = (post.authorName || '?').charAt(0).toUpperCase();
  const reactionEntries = Object.entries(post.reactions || {});
  const hasContent = post.content.trim().length > 0;

  // Avatar gradient cycles through a few palettes
  const avatarGradients = [
    'from-cyan-500 to-blue-600',
    'from-violet-500 to-purple-600',
    'from-teal-500 to-emerald-600',
    'from-amber-500 to-orange-600',
    'from-rose-500 to-pink-600',
  ];
  const avatarGrad = avatarGradients[index % avatarGradients.length];

  return (
    <div className="group relative px-4 py-4 border-b border-white/[0.04] hover:bg-white/[0.025] transition-all duration-200">
      {/* Left accent line */}
      <div className="absolute left-0 top-4 bottom-4 w-0.5 rounded-full bg-gradient-to-b from-cyan-500/60 to-violet-500/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="flex gap-3">
        {/* Prompt number + avatar stack */}
        <div className="flex flex-col items-center gap-1 shrink-0">
          <span className="text-[9px] font-bold text-tech-text-muted/40 tabular-nums leading-none">
            #{String(index + 1).padStart(2, '0')}
          </span>
          <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${avatarGrad} flex items-center justify-center text-white font-bold text-xs shadow-[0_2px_8px_rgba(0,0,0,0.3)]`}>
            {initials}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-2 justify-between mb-2">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-white">{post.authorName}</span>
              <span className="text-[10px] text-tech-text-muted/50">{relativeTime(post.createdAt)}</span>
            </div>
            {/* Copy button — shown on hover */}
            {hasContent && (
              <button
                type="button"
                onClick={handleCopy}
                title="Copy prompt"
                className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2 py-0.5 rounded-lg border border-white/10 text-tech-text-muted hover:text-white hover:border-white/20 bg-white/5 hover:bg-white/10 text-[10px] transition-all"
              >
                {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>

          {/* Prompt content — inset dark card */}
          {hasContent && (
            <div className="relative rounded-xl bg-[#060a16] border border-white/[0.06] px-4 py-3 mb-2 shadow-inner">
              <p className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed font-mono">
                {autoLinkText(post.content)}
              </p>
              {/* Subtle top gradient line */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent rounded-t-xl" />
            </div>
          )}

          {/* Tags row */}
          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {post.tags.map((tag, i) => (
                <TagChip key={i} tag={tag} />
              ))}
            </div>
          )}

          {/* Reactions + emoji button */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {reactionEntries.map(([emoji, userIds]) => {
              const has = userIds.includes(currentUserId);
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => addReaction(post.id, emoji, currentUserId, has).catch(console.error)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs border transition-all ${
                    has
                      ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300 shadow-[0_0_6px_rgba(6,182,212,0.2)]'
                      : 'bg-white/5 border-white/10 text-tech-text-muted hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <span>{emoji}</span>
                  <span>{userIds.length}</span>
                </button>
              );
            })}

            <div className="relative" ref={emojiRef}>
              <button
                type="button"
                onClick={() => setEmojiOpen((v) => !v)}
                className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-7 h-6 rounded-lg text-tech-text-muted hover:bg-white/10 hover:text-white transition-all"
                title="React"
              >
                <SmilePlus className="w-3.5 h-3.5" />
              </button>
              {emojiOpen && (
                <EmojiPalette
                  postId={post.id}
                  reactions={post.reactions}
                  currentUserId={currentUserId}
                  onClose={() => setEmojiOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tag toolbar buttons ──────────────────────────────────────────────────────

const TAG_BUTTONS: { type: PromptTagType; label: string; icon: typeof Link2; desc: string }[] = [
  { type: 'link',    label: 'Link',    icon: Link2,       desc: 'Attach a URL' },
  { type: 'keyword', label: 'Tag',     icon: Tag,         desc: 'Add a keyword' },
  { type: 'env_var', label: 'Env Var', icon: Settings2,   desc: 'Environment variable' },
  { type: 'status',  label: 'Status',  icon: AlertCircle, desc: 'Mark status' },
  { type: 'tool',    label: 'Tool',    icon: Wrench,      desc: 'Reference a tool' },
];

// ─── Tag popover ──────────────────────────────────────────────────────────────

function TagPopover({
  type,
  onAdd,
  onClose,
}: {
  type: PromptTagType;
  onAdd: (tag: PromptTag) => void;
  onClose: () => void;
}) {
  const [val, setVal] = useState('');
  const [toolSearch, setToolSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = () => {
    if (type === 'status') return;
    if (type === 'tool') {
      if (val.trim()) { onAdd({ type, value: val.trim() }); onClose(); }
      return;
    }
    if (val.trim()) { onAdd({ type, value: val.trim() }); onClose(); }
  };

  const filteredTools = POPULAR_TOOLS.filter((t) =>
    t.name.toLowerCase().includes(toolSearch.toLowerCase())
  ).slice(0, 15);

  if (type === 'status') {
    return (
      <div className="flex flex-col gap-1.5 p-3 w-52">
        <p className="text-[10px] font-bold uppercase tracking-widest text-tech-text-muted mb-1">Status</p>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => { onAdd({ type: 'status', value: s }); onClose(); }}
            className={`text-left px-3 py-2 rounded-lg text-xs font-medium border transition-all hover:scale-[1.02] active:scale-95 ${STATUS_CHIP_STYLES[s]}`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>
    );
  }

  if (type === 'tool') {
    return (
      <div className="flex flex-col gap-2 p-3 w-64">
        <p className="text-[10px] font-bold uppercase tracking-widest text-tech-text-muted mb-0.5">Tool</p>
        <input
          ref={inputRef}
          type="text"
          value={toolSearch}
          onChange={(e) => { setToolSearch(e.target.value); setVal(e.target.value); }}
          placeholder="Search tools…"
          className="w-full px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-teal-400/50 transition-colors"
          onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        />
        <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto custom-scrollbar">
          {filteredTools.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => { onAdd({ type: 'tool', value: t.name }); onClose(); }}
              className="px-2 py-0.5 rounded-lg border border-teal-500/30 bg-teal-500/10 text-teal-300 text-xs hover:bg-teal-500/20 hover:border-teal-400/40 transition-all"
            >
              {t.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const placeholder =
    type === 'link' ? 'https://github.com/...' :
    type === 'keyword' ? 'e.g. authentication' :
    'e.g. API_KEY';

  const accentColor =
    type === 'link' ? 'focus:border-cyan-400/50' :
    type === 'keyword' ? 'focus:border-violet-400/50' :
    'focus:border-amber-400/50';

  return (
    <div className="flex flex-col gap-2 p-3 w-60">
      <p className="text-[10px] font-bold uppercase tracking-widest text-tech-text-muted mb-0.5">
        {type === 'link' ? 'URL' : type === 'keyword' ? 'Keyword' : 'Variable name'}
      </p>
      <input
        ref={inputRef}
        type={type === 'link' ? 'url' : 'text'}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 ${type === 'env_var' ? 'font-mono' : ''} focus:outline-none ${accentColor} transition-colors`}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') onClose();
        }}
      />
      <button
        type="button"
        onClick={commit}
        disabled={!val.trim()}
        className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white disabled:opacity-30 transition-all bg-gradient-to-r ${TAG_ACCENT[type]} hover:shadow-lg active:scale-95`}
      >
        Add
      </button>
    </div>
  );
}

// ─── PromptBoard ─────────────────────────────────────────────────────────────

interface PromptBoardProps {
  taskId: string;
  currentUserId: string;
  currentUserName: string;
}

const PromptBoard: React.FC<PromptBoardProps> = ({ taskId, currentUserId, currentUserName }) => {
  const [posts, setPosts] = useState<PromptPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  const [content, setContent] = useState('');
  const [pendingTags, setPendingTags] = useState<PromptTag[]>([]);
  const [activeTagType, setActiveTagType] = useState<PromptTagType | null>(null);
  const [posting, setPosting] = useState(false);
  const [focused, setFocused] = useState(false);

  const feedEndRef = useRef<HTMLDivElement>(null);
  const tagPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoadingPosts(true);
    const unsub = subscribePromptPosts(taskId, (incoming) => {
      setPosts(incoming);
      setLoadingPosts(false);
    });
    return unsub;
  }, [taskId]);

  useEffect(() => {
    if (!activeTagType) return;
    const handler = (e: MouseEvent) => {
      if (tagPopoverRef.current && !tagPopoverRef.current.contains(e.target as Node)) {
        setActiveTagType(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeTagType]);

  const prevCount = useRef(0);
  useEffect(() => {
    if (posts.length > prevCount.current) {
      feedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevCount.current = posts.length;
  }, [posts.length]);

  const addTag = useCallback((tag: PromptTag) => {
    setPendingTags((prev) => [...prev, tag]);
  }, []);

  const removeTag = useCallback((idx: number) => {
    setPendingTags((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handlePost = async () => {
    if (!content.trim() && pendingTags.length === 0) return;
    setPosting(true);
    try {
      await createPromptPost({
        taskId,
        authorId: currentUserId,
        authorName: currentUserName,
        content: content.trim(),
        tags: pendingTags,
      });
      setContent('');
      setPendingTags([]);
    } catch (err) {
      console.error('Error posting:', err);
    } finally {
      setPosting(false);
    }
  };

  const displayPosts = [...posts].reverse();
  const canPost = content.trim().length > 0 || pendingTags.length > 0;

  return (
    <div className="flex flex-col h-full bg-[#05080f]">
      {/* ── Header ── */}
      <div className="shrink-0 px-4 py-3 border-b border-white/[0.06] bg-gradient-to-r from-[#070b18] to-[#060a14]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {/* Glowing icon */}
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-[0_0_12px_rgba(6,182,212,0.4)]">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-widest bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
                  Prompt Board
                </span>
                {/* Live indicator */}
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.8)] animate-pulse" />
                  <span className="text-[9px] text-emerald-400/70 font-medium uppercase tracking-wider">Live</span>
                </span>
              </div>
              <p className="text-[10px] text-tech-text-muted/50 leading-none mt-0.5">
                Prompts are the currency of AI collaboration
              </p>
            </div>
          </div>
          {displayPosts.length > 0 && (
            <span className="text-[10px] font-bold text-tech-text-muted/40 bg-white/5 border border-white/[0.06] px-2 py-0.5 rounded-full">
              {displayPosts.length} prompt{displayPosts.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* ── Feed ── */}
      <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0">
        {loadingPosts ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-tech-text-muted">
            <Loader className="w-5 h-5 animate-spin text-cyan-500" />
            <span className="text-xs">Loading prompts…</span>
          </div>
        ) : displayPosts.length === 0 ? (
          /* ── Premium empty state ── */
          <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
            <div className="relative mb-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-500/20 flex items-center justify-center shadow-[0_0_40px_rgba(6,182,212,0.12)]">
                <Zap className="w-9 h-9 text-cyan-400/70" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-[0_0_8px_rgba(245,158,11,0.5)]">
                <Sparkles className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <h3 className="text-sm font-bold text-white mb-1.5">Your Prompt Board is ready</h3>
            <p className="text-xs text-tech-text-muted/60 max-w-[220px] leading-relaxed">
              In the AI age, prompts are the most valuable artifacts of any project. Start crafting.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {(['link', 'keyword', 'env_var', 'tool'] as PromptTagType[]).map((t) => (
                <span key={t} className={`text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r ${TAG_ACCENT[t]} bg-opacity-10 text-white/50 border border-white/10`}>
                  {t.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>
        ) : (
          displayPosts.map((post, i) => (
            <PostCard key={post.id} post={post} index={i} currentUserId={currentUserId} />
          ))
        )}
        <div ref={feedEndRef} />
      </div>

      {/* ── Composer ── */}
      <div
        className={`shrink-0 border-t transition-all duration-300 ${
          focused
            ? 'border-cyan-500/30 bg-gradient-to-b from-[#080d1a] to-[#060a14] shadow-[0_-8px_32px_rgba(6,182,212,0.06)]'
            : 'border-white/[0.06] bg-[#060a14]'
        }`}
      >
        {/* Tag toolbar */}
        <div className="px-3 pt-3 flex items-center gap-1.5 flex-wrap">
          {TAG_BUTTONS.map(({ type, label, icon: Icon }) => (
            <div key={type} className="relative" ref={activeTagType === type ? tagPopoverRef : undefined}>
              <button
                type="button"
                title={TAG_BUTTONS.find(b => b.type === type)?.desc}
                onClick={() => setActiveTagType(activeTagType === type ? null : type)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-all active:scale-95 ${
                  activeTagType === type
                    ? `bg-gradient-to-r ${TAG_ACCENT[type]} text-white border-transparent shadow-lg`
                    : 'border-white/10 text-tech-text-muted hover:border-white/20 hover:text-white bg-white/[0.03] hover:bg-white/[0.06]'
                }`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
              {activeTagType === type && (
                <div className="absolute bottom-full left-0 mb-2 bg-[#0a0f1e] border border-white/[0.08] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] z-20 backdrop-blur-sm">
                  <TagPopover
                    type={type}
                    onAdd={addTag}
                    onClose={() => setActiveTagType(null)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pending tags row */}
        {pendingTags.length > 0 && (
          <div className="px-3 pt-2 flex flex-wrap gap-1.5">
            {pendingTags.map((tag, i) => (
              <TagChip key={i} tag={tag} onRemove={() => removeTag(i)} />
            ))}
          </div>
        )}

        {/* Textarea */}
        <div className="px-3 pt-2 pb-1 relative">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handlePost();
              }
            }}
            placeholder="Craft your prompt — describe context, constraints, expected output…"
            rows={3}
            className={`w-full px-4 py-3 rounded-xl text-sm text-white/90 placeholder-white/25 resize-none font-mono leading-relaxed focus:outline-none transition-all duration-300 ${
              focused
                ? 'bg-[#08101e] border border-cyan-500/40 shadow-[0_0_0_1px_rgba(6,182,212,0.15),inset_0_1px_0_rgba(6,182,212,0.05)]'
                : 'bg-white/[0.04] border border-white/[0.07] hover:border-white/[0.12]'
            }`}
          />
          {/* Glow effect when focused */}
          {focused && (
            <div className="absolute inset-x-3 bottom-1 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent pointer-events-none" />
          )}
        </div>

        {/* Footer: hint + post button */}
        <div className="px-3 pb-3 flex items-center justify-between gap-2">
          <span className="text-[10px] text-tech-text-muted/30 hidden sm:block">
            Ctrl+Enter to post
          </span>
          <button
            type="button"
            onClick={handlePost}
            disabled={posting || !canPost}
            className={`ml-auto flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-30 transition-all active:scale-95 ${
              canPost && !posting
                ? 'bg-gradient-to-r from-cyan-500 to-violet-600 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:scale-[1.02]'
                : 'bg-white/10'
            }`}
          >
            {posting ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Post Prompt
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptBoard;
